import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import type { Workday } from '../../api/types';
import { useInterval } from '../../hooks/useInterval';
import { getLocation, locationHelpText } from '../../hooks/useGeolocation';
import { useToast } from '../../components/Toast';
import { Modal } from '../../components/Modal';
import { fmtHM, fmtTime } from '../../utils/time';
import { computeDurationsClient, computeStatus } from '../../utils/workday';

export function TimeClockPage() {
  const { push } = useToast();
  const [workday, setWorkday] = useState<Workday|null>(null);
  const [clock, setClock] = useState(new Date());
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [geoHelp, setGeoHelp] = useState<string|null>(null);

  async function load() {
    const r = await api.get<{workday: Workday}>('/api/me/workdays/today');
    setWorkday(r.workday);
  }

  useEffect(() => { load().catch(() => {}); }, []);
  useInterval(() => setClock(new Date()), 1000);

  const status = workday ? computeStatus(workday) : 'NOT_STARTED';
  const dur = workday ? computeDurationsClient(workday, clock) : { workMin: 0, breakMin: 0, netMin: 0 };

  const mainCounter = fmtHM(dur.netMin);
  const breakCounter = fmtHM(dur.breakMin);

  async function safeLocation() {
    try {
      const loc = await getLocation();
      return loc;
    } catch (e:any) {
      setGeoHelp(locationHelpText());
      return null;
    }
  }

  async function doAction(action: 'start-work'|'start-break'|'end-break'|'end-work') {
    if (busyAction) return;
    setBusyAction(action);
    setGeoHelp(null);

    try {
      const location = await safeLocation();
      const r = await api.post<{workday: Workday}>(`/api/me/workdays/today/${action}`, location ? { location } : undefined);
      setWorkday(r.workday);

      // In alcuni browser (StrictMode/dev) può servire un refresh immediato dello stato.
      // Facciamo un sync leggero dopo un attimo.
      window.setTimeout(() => load().catch(() => {}), 400);

      push({ title: 'Timbratura registrata', body: location ? 'Operazione completata.' : 'Operazione completata (posizione non disponibile).' });
    } catch (e: any) {
      push({ title: 'Errore timbratura', body: e?.data?.error || e?.message });
    } finally {
      setBusyAction(null);
    }
  }

  const statusLabel = useMemo(() => {
    if (status === 'NOT_STARTED') return { text: 'Non ancora timbrato', badge: 'badgeGray' };
    if (status === 'IN_WORK') return { text: 'In Lavoro', badge: 'badgeGreen' };
    if (status === 'IN_BREAK') return { text: 'In Pausa', badge: 'badgeOrange' };
    return { text: 'Giornata chiusa', badge: 'badgeRed' };
  }, [status]);

  return (
    <div className="timeClockWrap">
      <div className="grid" style={{ gap: 16 }}>
      <div className="card">
        <div className="spread">
          <div>
            <div className="small">Ora</div>
            <div style={{ fontSize: 34, fontWeight: 900, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
              {clock.toLocaleTimeString('it-IT')}
            </div>
          </div>
          <div className={`badge ${statusLabel.badge}`}>{statusLabel.text}</div>
        </div>

        <div style={{ marginTop: 16 }}>
          <div className="small">Tempo netto</div>
          <div style={{ fontSize: 40, fontWeight: 900 }}>{mainCounter}</div>
          {status !== 'NOT_STARTED' ? (
            <div className="small">Lavoro (lordo): <b>{fmtHM(dur.workMin)}</b> · Pausa: <b>{breakCounter}</b></div>
          ) : null}
        </div>

        <div style={{ marginTop: 18, display:'grid', gap: 10 }}>
          {status === 'NOT_STARTED' ? (
            <button
              className="btn btnPrimary"
              disabled={!!busyAction}
              style={{ borderRadius: 999, height: 120, width: 120, justifySelf:'center' }}
              onClick={() => doAction('start-work')}
            >
              {busyAction ? '…' : 'Inizia'}
            </button>
          ) : null}

          {status === 'IN_WORK' ? (
            <div style={{ display:'grid', gap: 10 }}>
              <button className="btn btnPrimary" disabled={!!busyAction} onClick={() => doAction('start-break')}>
                {busyAction === 'start-break' ? '…' : 'Inizia Pausa'}
              </button>
              <button className="btn btnSecondary" disabled={!!busyAction} onClick={() => doAction('end-work')}>
                {busyAction === 'end-work' ? '…' : 'Termina Giornata'}
              </button>
            </div>
          ) : null}

          {status === 'IN_BREAK' ? (
            <button className="btn btnPrimary" disabled={!!busyAction} onClick={() => doAction('end-break')}>
              {busyAction === 'end-break' ? '…' : 'Termina Pausa'}
            </button>
          ) : null}

          {status === 'ENDED' ? (
            <div className="card" style={{ background: 'var(--gray-200)', boxShadow:'none' }}>
              <div className="small">Riepilogo</div>
              <div className="row" style={{ justifyContent:'space-between' }}>
                <span>Inizio</span><b>{fmtTime(workday?.start_work_at || null)}</b>
              </div>
              <div className="row" style={{ justifyContent:'space-between' }}>
                <span>Fine</span><b>{fmtTime(workday?.end_work_at || null)}</b>
              </div>
              <div className="row" style={{ justifyContent:'space-between' }}>
                <span>Netto</span><b>{fmtHM(dur.netMin)}</b>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <Modal open={!!geoHelp} onClose={() => setGeoHelp(null)} title="Posizione non disponibile">
        <div className="p">{geoHelp}</div>
        <div className="small">La timbratura viene comunque registrata; la posizione viene salvata quando disponibile.</div>
      </Modal>
      </div>
    </div>
  );
}
